import React, { useMemo, useState } from 'react';
import {
  Box, Checkbox, Chip, FormControlLabel, MenuItem, Select, Switch, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { BUCKETS } from '../../utils/reportSelection';

const MAX_GROUPS = 3;
const MAX_AGGS = 5;

/**
 * 필드 선택 - 분류(FIELD_GRP)별 목록. 검색 상자를 두지 않는다(R10).
 * 목록형(detail): 체크하면 열로 추가. 요약형(summary): "그룹으로"/"집계로" 버튼.
 *
 * 6번(Fable 재검토 2026-10-26): 서버가 내려주는 defaultVisible을 그대로 쓴다 - "단계(코드)"
 * 같은 코드 쌍 필드는 기본으로 숨기고, "코드 항목 보기" 토글로만 보이게 한다. 이미 선택/그룹/
 * 집계에 쓰인 필드는 숨겨도 혼란스러우니 토글과 무관하게 항상 보인다.
 */
const FieldPicker = ({ fields = [], mode, columns, onColumnsChange, groupBy, onGroupByChange, aggregations, onAggregationsChange }) => {
  const [showCodeFields, setShowCodeFields] = useState(false);

  const inUse = (fieldCd) =>
    columns.includes(fieldCd) || groupBy.some((g) => g.field === fieldCd) || aggregations.some((a) => a.field === fieldCd);

  const visibleFields = useMemo(
    () => fields.filter((f) => f.defaultVisible !== false || showCodeFields || inUse(f.fieldCd)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fields, showCodeFields, columns, groupBy, aggregations],
  );
  const hiddenCount = fields.length - visibleFields.length;

  const byGrp = visibleFields.reduce((acc, f) => {
    (acc[f.fieldGrp || '기타'] = acc[f.fieldGrp || '기타'] || []).push(f);
    return acc;
  }, {});
  const byCode = Object.fromEntries(fields.map((f) => [f.fieldCd, f]));

  const toggleColumn = (fieldCd) => {
    if (columns.includes(fieldCd)) {
      onColumnsChange(columns.filter((c) => c !== fieldCd));
    } else if (columns.length < 30) {
      onColumnsChange([...columns, fieldCd]);
    }
  };

  const addGroup = (fieldCd) => {
    if (groupBy.length >= MAX_GROUPS || groupBy.some((g) => g.field === fieldCd)) return;
    const field = byCode[fieldCd];
    onGroupByChange([...groupBy, { field: fieldCd, bucket: field?.bucketable ? 'month' : undefined }]);
  };
  const removeGroup = (fieldCd) => onGroupByChange(groupBy.filter((g) => g.field !== fieldCd));
  const setBucket = (fieldCd, bucket) =>
    onGroupByChange(groupBy.map((g) => (g.field === fieldCd ? { ...g, bucket } : g)));

  const addAgg = (fieldCd, func) => {
    if (aggregations.length >= MAX_AGGS) return;
    onAggregationsChange([...aggregations, { field: fieldCd, func }]);
  };
  const removeAgg = (idx) => onAggregationsChange(aggregations.filter((_, i) => i !== idx));

  const [pendingAggField, setPendingAggField] = useState('');

  return (
    <Box>
      {mode === 'summary' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">그룹 (최대 {MAX_GROUPS}개)</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5, mb: 1 }}>
            {groupBy.map((g) => (
              <Chip
                key={g.field}
                label={
                  byCode[g.field]?.bucketable
                    ? `${byCode[g.field]?.fieldNm} (${BUCKETS.find((b) => b.value === g.bucket)?.label || g.bucket})`
                    : byCode[g.field]?.fieldNm || g.field
                }
                onDelete={() => removeGroup(g.field)}
              />
            ))}
            {groupBy.length === 0 && <Typography variant="caption" color="text.disabled">아직 없음</Typography>}
          </Box>

          <Typography variant="caption" color="text.secondary">집계 (최대 {MAX_AGGS}개 - 행 수는 항상 가능)</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5, alignItems: 'center' }}>
            {aggregations.map((a, i) => (
              <Chip
                key={`${a.field}-${a.func}-${i}`}
                label={a.field === '*' ? '건수' : `${a.func}(${byCode[a.field]?.fieldNm || a.field})`}
                onDelete={() => removeAgg(i)}
              />
            ))}
            <Chip
              icon={<AddIcon fontSize="small" />}
              label="건수"
              variant="outlined"
              onClick={() => addAgg('*', 'COUNT')}
              disabled={aggregations.length >= MAX_AGGS}
            />
          </Box>
        </Box>
      )}

      {fields.some((f) => f.defaultVisible === false) && (
        <FormControlLabel
          sx={{ mb: 1 }}
          control={<Switch size="small" checked={showCodeFields} onChange={(e) => setShowCodeFields(e.target.checked)} />}
          label={`코드 항목 보기${hiddenCount > 0 ? ` (${hiddenCount}개 숨김)` : ''}`}
        />
      )}

      {Object.entries(byGrp).map(([grp, list]) => (
        <Box key={grp} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>{grp}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {list.map((f) => (
              <Box key={f.fieldCd} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {mode === 'detail' ? (
                  <FormControlLabel
                    control={<Checkbox size="small" checked={columns.includes(f.fieldCd)} onChange={() => toggleColumn(f.fieldCd)} />}
                    label={f.fieldNm}
                  />
                ) : (
                  <>
                    <Typography variant="body2" sx={{ minWidth: 140 }}>{f.fieldNm}</Typography>
                    {f.groupable && (
                      <Chip
                        size="small"
                        label="그룹으로"
                        clickable
                        disabled={groupBy.some((g) => g.field === f.fieldCd) || groupBy.length >= MAX_GROUPS}
                        onClick={() => addGroup(f.fieldCd)}
                      />
                    )}
                    {f.bucketable && groupBy.some((g) => g.field === f.fieldCd) && (
                      <Select
                        size="small"
                        value={groupBy.find((g) => g.field === f.fieldCd)?.bucket || 'month'}
                        onChange={(e) => setBucket(f.fieldCd, e.target.value)}
                      >
                        {BUCKETS.map((b) => (
                          <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
                        ))}
                      </Select>
                    )}
                    {f.allowedAggFuncs?.length > 0 && (
                      <>
                        <Select
                          size="small"
                          displayEmpty
                          value={pendingAggField === f.fieldCd ? pendingAggField : ''}
                          onChange={(e) => {
                            if (e.target.value) addAgg(f.fieldCd, e.target.value);
                            setPendingAggField('');
                          }}
                          onOpen={() => setPendingAggField(f.fieldCd)}
                          renderValue={() => '집계로'}
                        >
                          {f.allowedAggFuncs.map((fn) => (
                            <MenuItem key={fn} value={fn}>{fn}</MenuItem>
                          ))}
                        </Select>
                      </>
                    )}
                  </>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default FieldPicker;
